import React, { useState, useEffect, useCallback } from 'react';
import { Table, Card, Button, Space, Tag, Select, Input, message, Modal, Typography, Dropdown, Menu, Popconfirm, DatePicker, Checkbox, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, MoreOutlined, CheckCircleOutlined, ClockCircleOutlined, ShoppingCartOutlined, ShoppingOutlined, UploadOutlined, ReloadOutlined, CheckSquareOutlined, FilePdfOutlined, FileExcelOutlined, RobotOutlined, InboxOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { customerAPI, orderStatusAPI, customerOrderAPI, productAPI } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { Customer, OrderStatus, CustomerOrder, OrderCompleteness, Product } from '../types.ts';
import { formatCurrency } from '../utils/numberFormatting.ts';
import { generateProductDisplayName } from '../utils/productUtils.ts';
import ConvertToSupplierOrdersModal from '../components/ConvertToSupplierOrdersModal.tsx';
import CustomerOrderForm from '../components/CustomerOrderForm.tsx';
import OrderStatusModal from '../components/OrderStatusModal.tsx';
import OrderDetailsModal from '../components/OrderDetailsModal.tsx';
import OrderManagementModal from '../components/OrderManagementModal.tsx';
import CustomerOrderImportModal from '../components/CustomerOrderImportModal.tsx';
import AIOrderImportModal from '../components/AIOrderImportModal.tsx';
import FulfillOrderModal from '../components/FulfillOrderModal.tsx';
import StockOrderCoverageModal from '../components/StockOrderCoverageModal.tsx';

const { Option } = Select;
const { Title } = Typography;
const { RangePicker } = DatePicker;


const CustomerOrdersNew: React.FC = () => {
  const { canWrite } = useAuth();
  const canEdit = canWrite('customer-orders');
  const [orders, setOrders] = useState<CustomerOrder[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [orderStatuses, setOrderStatuses] = useState<OrderStatus[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [orderCompleteness, setOrderCompleteness] = useState<{[key: number]: OrderCompleteness}>({});
  const [loading, setLoading] = useState(false);
  const [isFormModalVisible, setIsFormModalVisible] = useState(false);
  const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
  const [isConvertModalVisible, setIsConvertModalVisible] = useState(false);
  const [isOrderManagementModalVisible, setIsOrderManagementModalVisible] = useState(false);
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [isAIImportModalVisible, setIsAIImportModalVisible] = useState(false);
  const [editingOrder, setEditingOrder] = useState<CustomerOrder | null>(null);
  const [statusChangingOrder, setStatusChangingOrder] = useState<CustomerOrder | null>(null);
  const [viewingOrder, setViewingOrder] = useState<CustomerOrder | null>(null);
  const [convertingOrder, setConvertingOrder] = useState<CustomerOrder | null>(null);
  const [managingOrder, setManagingOrder] = useState<CustomerOrder | null>(null);
  const [fulfillingOrder, setFulfillingOrder] = useState<CustomerOrder | null>(null);
  // true mentre un cambio stato/evasione è in corso: blocca i pulsanti dei modal
  // (un doppio clic su "Evadi" ha prodotto in passato movimenti OUT raddoppiati)
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [coverageOrder, setCoverageOrder] = useState<CustomerOrder | null>(null);
  const [filters, setFilters] = useState({
    customer_id: undefined as number | undefined,
    order_number: undefined as string | undefined,
    customer_order_number: undefined as string | undefined,
    order_status_id: undefined as number | undefined,
    product_id: undefined as number | undefined,
    date_from: undefined as string | undefined,
    date_to: undefined as string | undefined,
    show_completed: false,
  });

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const apiFilters = {
        ...filters,
        exclude_completed: !filters.show_completed,  // Se show_completed è false, escludi evasi e annullati
        limit: 0,  // 0 = elenco completo (prima il backend troncava ai 100 ordini più recenti)
      };
      const data = await customerOrderAPI.getCustomerOrders(apiFilters);
      setOrders(data);
      // Carica i dati di completeness per gli ordini
      loadOrderCompleteness(data);
    } catch (error) {
      message.error('Errore nel caricamento degli ordini');
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadOrderCompleteness = async (ordersList: CustomerOrder[]) => {
    try {
      // Gli ordini Evasi/Annullati non hanno evadibilità da calcolare: nessuna chiamata
      // (con Visualizza Ordini Evasi l'elenco completo sarebbe altrimenti centinaia di richieste)
      const completenessPromises = ordersList.filter(order => ![10, 99].includes(order.order_status_id)).map(order => 
        customerOrderAPI.checkOrderCompleteness(order.id)
          .then(data => ({ orderId: order.id, data }))
          .catch(error => {
            console.error(`Error loading completeness for order ${order.id}:`, error);
            return null;
          })
      );
      
      const results = await Promise.all(completenessPromises);
      const completenessMap: {[key: number]: OrderCompleteness} = {};
      
      results.forEach(result => {
        if (result) {
          completenessMap[result.orderId] = result.data;
        }
      });
      
      setOrderCompleteness(completenessMap);
    } catch (error) {
      console.error('Error loading order completeness:', error);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadOrders();
  }, [filters, loadOrders]);

  const loadInitialData = async () => {
    try {
      const [customersData, orderStatusesData, productsData] = await Promise.all([
        customerAPI.getCustomers(),
        orderStatusAPI.getOrderStatuses(),
        productAPI.getProducts()
      ]);
      setCustomers(customersData);
      setOrderStatuses(orderStatusesData);
      setProducts(productsData);
    } catch (error) {
      message.error('Errore nel caricamento dei dati iniziali');
      console.error('Error loading initial data:', error);
    }
  };

  const handleCreate = () => {
    setEditingOrder(null);
    setIsFormModalVisible(true);
  };

  const handleEdit = (order: CustomerOrder) => {
    setEditingOrder(order);
    setIsFormModalVisible(true);
  };

  const handleView = (order: CustomerOrder) => {
    setViewingOrder(order);
    setIsDetailsModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await customerOrderAPI.deleteCustomerOrder(id);
      message.success('Ordine eliminato con successo');
      loadOrders();
    } catch (error: any) {
      message.error(error?.response?.data?.detail || 'Errore nell\'eliminazione dell\'ordine');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingOrder) {
        await customerOrderAPI.updateCustomerOrder(editingOrder.id, values);
        message.success('Ordine aggiornato con successo');
      } else {
        await customerOrderAPI.createCustomerOrder(values);
        message.success('Ordine creato con successo');
      }
      setIsFormModalVisible(false);
      loadOrders();
    } catch (error: any) {
      message.error(error?.response?.data?.detail || 'Errore nel salvataggio dell\'ordine');
    }
  };

  const handleStatusChange = (order: CustomerOrder) => {
    setStatusChangingOrder(order);
    setIsStatusModalVisible(true);
  };

  const handleStatusUpdate = async (statusData: any) => {
    if (!statusChangingOrder || statusSubmitting) return;

    setStatusSubmitting(true);
    try {
      await customerOrderAPI.updateOrderStatus(statusChangingOrder.id, statusData);

      message.success('Stato ordine aggiornato con successo');
      setIsStatusModalVisible(false);
      
      // Se lo stato cambia in "Evaso" (ID=10), apri automaticamente la modale di modifica
      if (statusData.order_status_id === 10 || statusData.status_id === 10) {
        message.info('Apertura della modale di modifica per completare i dati di fatturazione...');
        
        // Ricarica l'ordine aggiornato dal server prima di aprire la modale
        try {
          const updatedOrder = await customerOrderAPI.getCustomerOrder(statusChangingOrder.id);
          setEditingOrder(updatedOrder);
          setIsFormModalVisible(true);
        } catch (fetchError) {
          console.error('Error fetching updated order:', fetchError);
          // Fallback: usa l'ordine esistente
          setEditingOrder(statusChangingOrder);
          setIsFormModalVisible(true);
        }
      }
      
      setStatusChangingOrder(null);
      loadOrders();
    } catch (error: any) {
      // Migliorato error handling per evitare [object Object]
      console.error('Status update error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        fullError: error
      });
      
      // Messaggio di errore più dettagliato con serializzazione sicura
      let errorMessage = 'Errore sconosciuto nell\'aggiornamento dello stato';
      
      try {
        if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        } else if (typeof error === 'object') {
          errorMessage = JSON.stringify(error, null, 2);
        }
      } catch (serializationError) {
        console.error('Errore nella serializzazione dell\'errore:', serializationError);
        errorMessage = 'Errore di comunicazione con il server';
      }
      
      message.error(`Errore: ${errorMessage}`);
    } finally {
      setStatusSubmitting(false);
    }
  };

  const handleFulfillOrder = (order: CustomerOrder) => {
    setFulfillingOrder(order);
  };

  const handleConfirmFulfillOrder = async (fulfillmentData: {
    ddt_number?: string;
    invoice_number?: string;
    invoice_date?: string;
    t1_number?: string;
    t1_date?: string;
  }) => {
    if (!fulfillingOrder || statusSubmitting) return;

    setStatusSubmitting(true);
    try {
      const statusData = {
        status_id: 10,
        ...fulfillmentData
      };

      await customerOrderAPI.updateOrderStatus(fulfillingOrder.id, statusData);
      message.success('Ordine evaso con successo');
      setFulfillingOrder(null);
      loadOrders();
    } catch (error: any) {
      console.error('Fulfill order error:', error);

      let errorMessage = 'Errore nell\'evasione dell\'ordine';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }

      message.error(`Errore: ${errorMessage}`);
    } finally {
      setStatusSubmitting(false);
    }
  };

  const handleConvertToSupplierOrders = (order: CustomerOrder) => {
    setConvertingOrder(order);
    setIsConvertModalVisible(true);
  };

  const handleConvertModalClose = () => {
    setIsConvertModalVisible(false);
    setConvertingOrder(null);
    // Ricarica gli ordini per aggiornare eventuali cambiamenti
    loadOrders();
  };

  const handleOrderManagement = (order: CustomerOrder) => {
    setManagingOrder(order);
    setIsOrderManagementModalVisible(true);
  };

  const handleOrderManagementClose = () => {
    setIsOrderManagementModalVisible(false);
    setManagingOrder(null);
    // Ricarica gli ordini per aggiornare eventuali cambiamenti
    loadOrders();
  };

  const handleDownloadPdfRichiestaColli = async (order: CustomerOrder) => {
    try {
      const response = await customerOrderAPI.downloadPdfRichiestaColli(order.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `richiesta_colli_ordine_${order.customer_order_number || order.order_number || order.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('PDF Richiesta Colli scaricato con successo');
    } catch (error: any) {
      let errorMsg = 'Errore durante la generazione del PDF';
      if (error?.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          errorMsg = json.detail || errorMsg;
        } catch {}
      } else if (error?.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      }
      message.error(errorMsg);
    }
  };

  const handleDownloadXlsRichiestaColli = async (order: CustomerOrder) => {
    try {
      const response = await customerOrderAPI.downloadXlsRichiestaColli(order.id);
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `richiesta_colli_ordine_${order.customer_order_number || order.order_number || order.id}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('Excel Richiesta Colli scaricato con successo');
    } catch (error: any) {
      let errorMsg = 'Errore durante la generazione del file Excel';
      if (error?.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          errorMsg = json.detail || errorMsg;
        } catch {}
      } else if (error?.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      }
      message.error(errorMsg);
    }
  };

  const handleImportSuccess = () => {
    setIsImportModalVisible(false);
    // Ricarica gli ordini per mostrare il nuovo ordine importato
    loadOrders();
  };

  const handleDateRangeChange = (dates: any, dateStrings: string[]) => {
    setFilters({
      ...filters,
      date_from: dateStrings[0] || undefined,
      date_to: dateStrings[1] || undefined,
    });
  };

  const getStatusColor = (statusName: string) => {
    const colors: { [key: string]: string } = {
      'Nuovo': 'blue',
      'In Lavorazione': 'orange',
      'Confermato': 'green',
      'Pronto per Spedizione': 'cyan',
      'Spedito': 'purple',
      'Consegnato': 'lime',
      'Annullato': 'red',
    };
    return colors[statusName] || 'default';
  };

  const getActionMenu = (record: CustomerOrder) => {
    // Ordine "bloccato": evaso o in fase di spedizione (stato >= 6). Le righe non sono più
    // modificabili e l'ordine non è eliminabile (coerente con le guardie backend); Modifica
    // resta disponibile in modalità "solo testata" (dati di fatturazione compresi).
    const statusId = record.order_status_id ?? 0;
    // Righe bloccate: evasione avviata (6-10) o Annullato (99, nessuna riga modificabile).
    // Elimina bloccata SOLO per l'evasione (6-10): un ordine annullato non ha più
    // prenotazioni ed è eliminabile (coerente con _customer_order_is_locked del backend).
    const isLocked = statusId >= 6;
    const deleteLocked = statusId >= 6 && statusId <= 10;
    return (
    <Menu>
      <Menu.Item key="view" icon={<EyeOutlined />} onClick={() => handleView(record)}>
        Visualizza
      </Menu.Item>
      {canEdit && (
        <Menu.Item
          key="edit"
          icon={<EditOutlined />}
          title={isLocked ? 'Ordine evaso o in spedizione: modificabili solo testata e dati di fatturazione' : undefined}
          onClick={() => handleEdit(record)}
        >
          {isLocked ? 'Modifica (solo testata)' : 'Modifica'}
        </Menu.Item>
      )}
      {canEdit && (
        <Menu.Item key="status" icon={<CheckCircleOutlined />} onClick={() => handleStatusChange(record)}>
          Cambia Stato
        </Menu.Item>
      )}
      {canEdit && record.order_status_id === 3 && (
        <Menu.Item key="convert" icon={<ShoppingCartOutlined />} onClick={() => handleConvertToSupplierOrders(record)}>
          Trasforma in Ordine Fornitore
        </Menu.Item>
      )}
      {canEdit && (
        <Menu.Item key="management" icon={<ShoppingOutlined />} onClick={() => handleOrderManagement(record)}>
          Gestione Ordine
        </Menu.Item>
      )}
      {record.reorder && (
        <Menu.Item key="coverage" icon={<InboxOutlined />} onClick={() => setCoverageOrder(record)}>
          Copertura Ordine Stock
        </Menu.Item>
      )}
      <Menu.Item key="pdf-richiesta-colli" icon={<FilePdfOutlined />} onClick={() => handleDownloadPdfRichiestaColli(record)}>
        Pdf Richiesta Colli
      </Menu.Item>
      <Menu.Item key="xls-richiesta-colli" icon={<FileExcelOutlined />} onClick={() => handleDownloadXlsRichiestaColli(record)}>
        Xls Richiesta Colli
      </Menu.Item>
      {canEdit && orderCompleteness[record.id]?.can_fulfill && (
        <Menu.Item 
          key="fulfill" 
          icon={<CheckSquareOutlined />} 
          style={{ color: '#52c41a' }}
        >
          <Popconfirm
            title={record.reorder ? "Sei sicuro di voler evadere questo ordine stock?" : "Sei sicuro di voler evadere questo ordine?"}
            description={record.reorder ? 
              "L'ordine stock verrà marcato come evaso e i prodotti rimarranno disponibili in giacenza." :
              "L'ordine verrà marcato come evaso e non potrà più essere modificato."
            }
            onConfirm={() => handleFulfillOrder(record)}
            okText="Sì, evadi"
            cancelText="Annulla"
            okButtonProps={{ type: 'primary', style: { backgroundColor: '#52c41a' } }}
          >
            {record.reorder ? 'Evadi Ordine Stock' : 'Evadi Ordine'}
          </Popconfirm>
        </Menu.Item>
      )}
      {canEdit && <Menu.Divider />}
      {canEdit && (
        <Menu.Item key="delete" danger disabled={deleteLocked} icon={<DeleteOutlined />}>
          <Popconfirm
            title="Sei sicuro di voler eliminare questo ordine?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sì"
            cancelText="No"
            disabled={deleteLocked}
          >
            Elimina
          </Popconfirm>
        </Menu.Item>
      )}
    </Menu>
    );
  };

  const columns = [
    {
      title: 'Numero Ordine',
      dataIndex: 'order_number',
      key: 'order_number',
      render: (orderNumber: string) => <strong>{orderNumber}</strong>,
    },
    {
      title: 'Numero Ordine Cliente',
      dataIndex: 'customer_order_number',
      key: 'customer_order_number',
      render: (customerOrderNumber: string) => customerOrderNumber || '-',
    },
    {
      title: 'Cliente',
      dataIndex: ['customer', 'company'],
      key: 'customer',
    },
    {
      title: 'Data Ordine',
      dataIndex: 'order_date',
      key: 'order_date',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Data Consegna',
      dataIndex: 'requested_delivery_date',
      key: 'requested_delivery_date',
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
    },
    {
      title: 'Stato',
      dataIndex: ['order_status', 'status_name'],
      key: 'status',
      render: (statusName: string) => (
        <Tag color={getStatusColor(statusName)}>
          {statusName}
        </Tag>
      ),
    },
    {
      title: 'Ordine Stock',
      dataIndex: 'reorder',
      key: 'reorder',
      render: (reorder: boolean) => (
        reorder ? (
          <Tag color="orange" icon={<ReloadOutlined />}>
            ORDINE STOCK
          </Tag>
        ) : (
          <span style={{ color: '#999' }}>-</span>
        )
      ),
    },
    {
      title: 'Importo Totale',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount: number) => amount ? `€ ${formatCurrency(amount)}` : '-',
    },
    {
      title: 'Scadenza',
      key: 'urgency',
      render: (record: CustomerOrder) => {
        if (record.order_status_id === 10) {
          const evasionDate = record.updated_at ? dayjs(record.updated_at).format('DD/MM/YYYY') : '-';
          return <Tag color="blue">Evaso il {evasionDate}</Tag>;
        }
        if (!record.requested_delivery_date) return '-';
        const today = new Date();
        const deliveryDate = new Date(record.requested_delivery_date);
        const daysUntilDelivery = Math.ceil((deliveryDate.getTime() - today.getTime()) / (1000 * 3600 * 24));

        if (daysUntilDelivery < 0) {
          return <Tag color="red" icon={<ClockCircleOutlined />}>In Ritardo</Tag>;
        } else if (daysUntilDelivery <= 3) {
          return <Tag color="orange" icon={<ClockCircleOutlined />}>Urgente</Tag>;
        } else if (daysUntilDelivery <= 7) {
          return <Tag color="yellow">Prossimo</Tag>;
        }
        return <Tag color="green">Normale</Tag>;
      },
    },
    {
      title: 'Evadibilità',
      key: 'fulfillment',
      width: 120,
      align: 'center' as const,
      render: (record: CustomerOrder) => {
        if (record.order_status_id === 10) return <Tag color="green">Evaso</Tag>;
        if (record.order_status_id === 99) return <Tag color="red">Annullato</Tag>;
        const completeness = orderCompleteness[record.id];
        if (!completeness) {
          return <Tag color="default">Verifica...</Tag>;
        }
        
        if (completeness.can_fulfill) {
          return (
            <Tag color="green" icon={<CheckCircleOutlined />}>
              PRONTO
            </Tag>
          );
        } else {
          const percentage = Math.round(completeness.total_completion_percentage);
          return (
            <Tag color="orange">
              {percentage}%
            </Tag>
          );
        }
      },
    },
    {
      title: 'Azioni',
      key: 'actions',
      render: (_: any, record: CustomerOrder) => (
        <Dropdown overlay={getActionMenu(record)} trigger={['click']}>
          <Button icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  // Ordine in modifica già evaso/in spedizione (stato >= 6): il form apre in modalità
  // "solo testata" (righe in sola lettura, niente order_details nel payload)
  const editingLocked = !!editingOrder && (editingOrder.order_status_id ?? 0) >= 6;

  return (
    <div>
      <Title level={2}>Gestione Ordini Cliente</Title>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Space wrap>
            <Input
              placeholder="Filtra per Numero Ordine"
              style={{ width: 200 }}
              value={filters.order_number}
              onChange={(e) => setFilters({ ...filters, order_number: e.target.value || undefined })}
              allowClear
            />

            <Input
              placeholder="Filtra per N. Ordine Cliente"
              style={{ width: 200 }}
              value={filters.customer_order_number}
              onChange={(e) => setFilters({ ...filters, customer_order_number: e.target.value || undefined })}
              allowClear
            />

            <Select
              placeholder="Filtra per Cliente"
              style={{ width: 200 }}
              value={filters.customer_id}
              onChange={(value) => setFilters({ ...filters, customer_id: value })}
              allowClear
            >
              {customers.map((customer) => (
                <Option key={customer.id} value={customer.id}>
                  {customer.company}
                </Option>
              ))}
            </Select>

            <Select
              placeholder="Filtra per Prodotto"
              style={{ width: 280 }}
              value={filters.product_id}
              onChange={(value) => setFilters({ ...filters, product_id: value })}
              showSearch
              optionFilterProp="children"
              allowClear
            >
              {products.map((product) => (
                <Option key={product.id} value={product.id}>
                  {generateProductDisplayName(product.internal_product_code, product.size, product.color, product.product_description)}
                </Option>
              ))}
            </Select>

            <Select
              placeholder="Filtra per Stato"
              style={{ width: 150 }}
              value={filters.order_status_id}
              onChange={(value) => setFilters({ ...filters, order_status_id: value })}
              allowClear
            >
              {orderStatuses.map((status) => (
                <Option key={status.id} value={status.id}>
                  {status.status_name}
                </Option>
              ))}
            </Select>
            
            <RangePicker
              format="DD/MM/YYYY"
              placeholder={['Data Da', 'Data A']}
              onChange={handleDateRangeChange}
              value={[
                filters.date_from ? dayjs(filters.date_from) : null,
                filters.date_to ? dayjs(filters.date_to) : null
              ]}
            />
            
            <Checkbox
              checked={filters.show_completed}
              onChange={(e) => setFilters({ ...filters, show_completed: e.target.checked })}
            >
              Visualizza Ordini Evasi/Annullati:
            </Checkbox>
            
            {canEdit && (
              <Button
                type="default"
                icon={<UploadOutlined />}
                onClick={() => setIsImportModalVisible(true)}
              >
                Importa da XLS Oron
              </Button>
            )}

            {canEdit && (
              <Tooltip title="Carica l'ordine di acquisto (PO) ricevuto dal cliente tramite AI: PDF, Word, Excel o immagine">
                <Button
                  type="default"
                  icon={<RobotOutlined />}
                  onClick={() => setIsAIImportModalVisible(true)}
                >
                  Importa con AI
                </Button>
              </Tooltip>
            )}

            {canEdit && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
              >
                Nuovo Ordine
              </Button>
            )}
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={orders}
          rowKey="id"
          loading={loading}
          pagination={{
            total: orders.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
          scroll={{ x: true }}
        />

        {/* Modal Form Ordine (ordine evaso/in spedizione: solo testata e dati di fatturazione) */}
        {CustomerOrderForm && (
          <Modal
            title={
              editingOrder
                ? (editingLocked ? 'Modifica Ordine (solo testata e dati di fatturazione)' : 'Modifica Ordine')
                : 'Nuovo Ordine'
            }
            open={isFormModalVisible}
            onCancel={() => setIsFormModalVisible(false)}
            footer={null}
            width="90%"
            destroyOnClose
          >
            <CustomerOrderForm
              initialValues={editingOrder}
              headerOnly={editingLocked}
              customers={customers}
              orderStatuses={orderStatuses}
              onSubmit={handleSubmit}
              onCancel={() => setIsFormModalVisible(false)}
            />
          </Modal>
        )}

        {/* Modal Cambio Stato - Non implementato */}
        {OrderStatusModal && (
          <Modal
            title="Cambia Stato Ordine"
            open={isStatusModalVisible}
            onCancel={() => setIsStatusModalVisible(false)}
            footer={null}
            destroyOnClose
          >
            <OrderStatusModal
              order={statusChangingOrder}
              orderStatuses={orderStatuses}
              onSubmit={handleStatusUpdate}
              onCancel={() => setIsStatusModalVisible(false)}
              loading={statusSubmitting}
            />
          </Modal>
        )}

        {/* Modal Dettagli Ordine - Non implementato */}
        {OrderDetailsModal && (
          <Modal
            title="Dettagli Ordine"
            open={isDetailsModalVisible}
            onCancel={() => setIsDetailsModalVisible(false)}
            footer={null}
            width={1000}
            destroyOnClose
          >
            <OrderDetailsModal
              order={viewingOrder}
              onClose={() => setIsDetailsModalVisible(false)}
            />
          </Modal>
        )}

        {/* Modal Conversione Ordine Fornitore */}
        {convertingOrder && (
          <ConvertToSupplierOrdersModal
            visible={isConvertModalVisible}
            onClose={handleConvertModalClose}
            customerOrderId={convertingOrder.id}
            customerOrderNumber={convertingOrder.order_number}
          />
        )}

        {/* Modal Gestione Ordine */}
        <OrderManagementModal
          visible={isOrderManagementModalVisible}
          onClose={handleOrderManagementClose}
          order={managingOrder}
          onOrderUpdated={loadOrders}
        />

        {/* Modal Copertura Ordine Stock */}
        <StockOrderCoverageModal
          visible={coverageOrder !== null}
          order={coverageOrder}
          onClose={() => setCoverageOrder(null)}
          onCoverageChanged={loadOrders}
        />

        {/* Modal Evasione Ordine */}
        <FulfillOrderModal
          visible={fulfillingOrder !== null}
          order={fulfillingOrder}
          onCancel={() => setFulfillingOrder(null)}
          onConfirm={handleConfirmFulfillOrder}
          loading={statusSubmitting}
        />

        {/* Modal Importazione Ordine da XLS */}
        <CustomerOrderImportModal
          visible={isImportModalVisible}
          onCancel={() => setIsImportModalVisible(false)}
          onSuccess={handleImportSuccess}
        />

        {/* Modal Carica Ordine Cliente con AI */}
        <AIOrderImportModal
          visible={isAIImportModalVisible}
          onClose={() => setIsAIImportModalVisible(false)}
          onSuccess={loadOrders}
        />
      </Card>
    </div>
  );
};

export default CustomerOrdersNew;
